import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { format, isToday, isSameDay } from 'date-fns';
import Modal from 'react-modal';

Modal.setAppElement('#root');

// ─── Tokens ──────────────────────────────────────────────────────────────────

const COLOR = {
  bg: '#f7f7f8',
  surface: '#ffffff',
  border: '#e8e8eb',
  borderStrong: '#d0d0d5',
  text: '#111111',
  textMuted: '#888888',
  textLight: '#bbb',
  today: '#111111',
  todayBg: '#f0f0f0',
  accent: '#111111',
  green: '#22c55e',
  greenBg: '#f0fdf4',
  orange: '#f97316',
  orangeBg: '#fff7ed',
  red: '#ef4444',
  redBg: '#fef2f2',
  amber: '#f59e0b',
  amberBg: '#fffbeb',
  blue: '#3b82f6',
  blueBg: '#eff6ff',
  shadow: '0 1px 3px rgba(0,0,0,0.08)',
  shadowMd: '0 4px 12px rgba(0,0,0,0.10)',
};

// ─── Layout ───────────────────────────────────────────────────────────────────

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: ${COLOR.bg};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: ${COLOR.text};
  overflow: hidden;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  background: ${COLOR.surface};
  border-bottom: 1px solid ${COLOR.border};
  flex-shrink: 0;
  gap: 16px;
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PageTitle = styled.h1`
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.3px;
  margin: 0;
`;

const TabBar = styled.div`
  display: flex;
  gap: 2px;
  background: ${COLOR.bg};
  border-radius: 8px;
  padding: 3px;
`;

const Tab = styled.button`
  padding: 6px 14px;
  border-radius: 6px;
  border: none;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  background: ${({ active }) => (active ? COLOR.surface : 'transparent')};
  color: ${({ active }) => (active ? COLOR.text : COLOR.textMuted)};
  box-shadow: ${({ active }) => (active ? COLOR.shadow : 'none')};
`;

const WeekNav = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const NavBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid ${COLOR.border};
  background: ${COLOR.surface};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: ${COLOR.textMuted};
  transition: background 0.12s;
  &:hover { background: ${COLOR.bg}; color: ${COLOR.text}; }
`;

const WeekLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  min-width: 180px;
  text-align: center;
`;

const TodayBtn = styled.button`
  padding: 5px 12px;
  border-radius: 6px;
  border: 1px solid ${COLOR.border};
  background: ${COLOR.surface};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: ${COLOR.textMuted};
  &:hover { background: ${COLOR.bg}; color: ${COLOR.text}; }
`;

const AddBtn = styled.button`
  padding: 7px 16px;
  border-radius: 7px;
  border: none;
  background: ${COLOR.text};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: opacity 0.12s;
  &:hover { opacity: 0.85; }
`;

const PublishBtn = styled.button`
  padding: 7px 16px;
  border-radius: 7px;
  border: 2px solid ${COLOR.green};
  background: ${({ disabled }) => (disabled ? COLOR.bg : COLOR.greenBg)};
  color: ${COLOR.green};
  font-size: 13px;
  font-weight: 700;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.12s;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  &:hover { background: ${({ disabled }) => (disabled ? COLOR.bg : '#dcfce7')}; }
`;

const DraftBadge = styled.span`
  background: ${COLOR.orange};
  color: #fff;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 6px;
`;

// ─── Main content ─────────────────────────────────────────────────────────────

const Content = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

// ─── Week Grid ────────────────────────────────────────────────────────────────

const GridWrapper = styled.div`
  flex: 1;
  overflow: auto;
  padding: 20px 24px 24px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 180px repeat(7, 1fr);
  min-width: 900px;
  border-radius: 10px;
  border: 1px solid ${COLOR.border};
  background: ${COLOR.surface};
  overflow: hidden;
  box-shadow: ${COLOR.shadow};
`;

// Header row
const GridHeader = styled.div`
  display: contents;
`;

const GridCorner = styled.div`
  background: ${COLOR.bg};
  border-bottom: 1px solid ${COLOR.borderStrong};
  border-right: 1px solid ${COLOR.border};
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const CornerLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${COLOR.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DayHeader = styled.div`
  background: ${({ isToday }) => (isToday ? COLOR.todayBg : COLOR.bg)};
  border-bottom: 1px solid ${COLOR.borderStrong};
  border-left: 1px solid ${COLOR.border};
  padding: 10px 12px 8px;
  cursor: pointer;
  transition: background 0.1s;
  &:hover { background: #ebebeb; }
`;

const DayName = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: ${({ isToday }) => (isToday ? COLOR.accent : COLOR.textMuted)};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DayNum = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${({ isToday }) => (isToday ? COLOR.accent : COLOR.text)};
  line-height: 1.2;
`;

const CoverPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  padding: 2px 6px;
  background: ${({ warn }) => (warn ? COLOR.orangeBg : COLOR.blueBg)};
  color: ${({ warn }) => (warn ? COLOR.orange : COLOR.blue)};
`;

// Employee rows
const EmployeeRow = styled.div`
  display: contents;
`;

const EmployeeCell = styled.div`
  padding: 10px 14px;
  border-bottom: 1px solid ${COLOR.border};
  border-right: 1px solid ${COLOR.border};
  background: ${COLOR.surface};
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const EmployeeName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${COLOR.text};
`;

const EmployeeRole = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: ${COLOR.textMuted};
`;

const ShiftCell = styled.div`
  padding: 6px 8px;
  border-bottom: 1px solid ${COLOR.border};
  border-left: 1px solid ${COLOR.border};
  background: ${COLOR.surface};
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 64px;
  cursor: pointer;
  transition: background 0.1s;
  &:hover { background: ${COLOR.bg}; }
`;

const ShiftCard = styled.div`
  border-radius: 6px;
  padding: 5px 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border-left: 3px solid ${({ roleColor }) => roleColor || '#6366f1'};
  background: ${({ roleColor }) => roleColor ? `${roleColor}14` : '#f0f0ff'};
  color: ${COLOR.text};
  opacity: ${({ draft }) => (draft ? 0.65 : 1)};
  transition: opacity 0.1s, transform 0.1s;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  &:hover { opacity: 1; transform: translateY(-1px); }
`;

const ShiftTime = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${COLOR.textMuted};
`;

const DraftDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${COLOR.orange};
  flex-shrink: 0;
`;

const AddShiftBtn = styled.button`
  border: none;
  background: none;
  font-size: 18px;
  color: ${COLOR.textLight};
  cursor: pointer;
  line-height: 1;
  padding: 0;
  margin-top: auto;
  align-self: flex-end;
  transition: color 0.1s;
  &:hover { color: ${COLOR.textMuted}; }
`;

// ─── Panels (Time-off + Trades) ───────────────────────────────────────────────

const PanelWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
`;

const PanelTitle = styled.h2`
  font-size: 15px;
  font-weight: 700;
  margin: 0 0 16px;
`;

const RequestList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RequestCard = styled.div`
  background: ${COLOR.surface};
  border: 1px solid ${COLOR.border};
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-shadow: ${COLOR.shadow};
`;

const RequestInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const RequestName = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const RequestMeta = styled.div`
  font-size: 12px;
  color: ${COLOR.textMuted};
`;

const StatusBadge = styled.span`
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: ${({ status }) =>
    status === 'approved' ? COLOR.greenBg :
    status === 'denied' ? COLOR.redBg :
    COLOR.amberBg};
  color: ${({ status }) =>
    status === 'approved' ? COLOR.green :
    status === 'denied' ? COLOR.red :
    COLOR.amber};
`;

const ActionBtns = styled.div`
  display: flex;
  gap: 8px;
`;

const ApproveBtn = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  background: ${COLOR.greenBg};
  color: ${COLOR.green};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: #dcfce7; }
`;

const DenyBtn = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  background: ${COLOR.redBg};
  color: ${COLOR.red};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: #fee2e2; }
`;

// ─── Shift Modal ──────────────────────────────────────────────────────────────

const modalStyle = {
  overlay: {
    backgroundColor: 'rgba(0,0,0,0.4)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    position: 'relative',
    inset: 'auto',
    border: 'none',
    borderRadius: '14px',
    padding: 0,
    maxWidth: '440px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    overflow: 'hidden',
  },
};

const ModalHeader = styled.div`
  padding: 20px 24px 16px;
  border-bottom: 1px solid ${COLOR.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  margin: 0;
`;

const CloseBtn = styled.button`
  border: none;
  background: none;
  font-size: 20px;
  color: ${COLOR.textMuted};
  cursor: pointer;
  line-height: 1;
  padding: 0;
  &:hover { color: ${COLOR.text}; }
`;

const ModalBody = styled.div`
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${COLOR.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border-radius: 8px;
  border: 1px solid ${COLOR.borderStrong};
  font-size: 14px;
  color: ${COLOR.text};
  background: ${COLOR.surface};
  outline: none;
  width: 100%;
  &:focus { border-color: ${COLOR.accent}; }
`;

const Input = styled.input`
  padding: 9px 12px;
  border-radius: 8px;
  border: 1px solid ${COLOR.borderStrong};
  font-size: 14px;
  color: ${COLOR.text};
  background: ${COLOR.surface};
  outline: none;
  width: 100%;
  box-sizing: border-box;
  &:focus { border-color: ${COLOR.accent}; }
`;

const TextArea = styled.textarea`
  padding: 9px 12px;
  border-radius: 8px;
  border: 1px solid ${COLOR.borderStrong};
  font-size: 14px;
  color: ${COLOR.text};
  background: ${COLOR.surface};
  outline: none;
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  min-height: 60px;
  &:focus { border-color: ${COLOR.accent}; }
`;

const ModalFooter = styled.div`
  padding: 16px 24px 20px;
  border-top: 1px solid ${COLOR.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const SaveBtn = styled.button`
  padding: 9px 20px;
  border-radius: 8px;
  border: none;
  background: ${COLOR.text};
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  &:hover { opacity: ${({ disabled }) => (disabled ? 0.5 : 0.85)}; }
`;

const DeleteShiftBtn = styled.button`
  padding: 9px 16px;
  border-radius: 8px;
  border: none;
  background: ${COLOR.redBg};
  color: ${COLOR.red};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: #fee2e2; }
`;

// ─── Publish Confirm Modal ────────────────────────────────────────────────────

const ConfirmModal = styled.div`
  padding: 24px;
  max-width: 380px;
  text-align: center;
`;

const ConfirmTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 10px;
`;

const ConfirmText = styled.p`
  font-size: 14px;
  color: ${COLOR.textMuted};
  margin: 0 0 20px;
  line-height: 1.5;
`;

const ConfirmActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const CancelBtn = styled.button`
  padding: 9px 20px;
  border-radius: 8px;
  border: 1px solid ${COLOR.border};
  background: ${COLOR.surface};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  color: ${COLOR.textMuted};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${COLOR.textMuted};
  font-size: 14px;
`;

// ─── Main View Component ──────────────────────────────────────────────────────

const SchedulingView = ({
  roles, employees, shifts, weekDays, weekStartStr, weekEndStr,
  reservationCounts, timeOffRequests, shiftTrades, draftCount, activeTab,
  loading, saving, publishing,
  shiftModalOpen, editingShift, shiftFormData,
  publishConfirmOpen,
  onPrevWeek, onNextWeek, onGoToToday,
  onOpenCreateShift, onOpenEditShift, onCloseShiftModal,
  onShiftFormChange, onSaveShift, onDeleteShift,
  onOpenPublishConfirm, onClosePublishConfirm, onPublishWeek,
  onSetActiveTab, onReviewTimeOff, onReviewTrade,
  getStaffingSuggestion,
  // Employee modal
  employeeModalOpen, editingEmployee, employeeForm,
  onOpenEmployeeModal, onCloseEmployeeModal, onEmployeeFormChange, onSaveEmployee, onDeleteEmployee,
  // Role modal
  roleModalOpen, editingRole, roleForm,
  onOpenRoleModal, onCloseRoleModal, onRoleFormChange, onSaveRole, onDeleteRole,
}) => {
  const weekLabel = useMemo(() => {
    if (!weekDays.length) return '';
    const s = weekDays[0];
    const e = weekDays[6];
    if (s.getMonth() === e.getMonth()) {
      return `${format(s, 'MMM d')} – ${format(e, 'd, yyyy')}`;
    }
    return `${format(s, 'MMM d')} – ${format(e, 'MMM d, yyyy')}`;
  }, [weekDays]);

  // Shifts indexed by "employeeId|dateStr"
  const shiftIndex = useMemo(() => {
    const idx = {};
    shifts.forEach(s => {
      const key = `${s.employee_id}|${s.shift_date}`;
      if (!idx[key]) idx[key] = [];
      idx[key].push(s);
    });
    return idx;
  }, [shifts]);

  const pendingTimeOff = timeOffRequests.filter(r => r.status === 'pending');
  const pendingTrades = shiftTrades.filter(t => t.status === 'manager_review');

  return (
    <Page>
      {/* ── Top Bar ── */}
      <TopBar>
        <TopBarLeft>
          <PageTitle>Scheduling</PageTitle>
          <TabBar>
            <Tab active={activeTab === 'schedule'} onClick={() => onSetActiveTab('schedule')}>
              Schedule
            </Tab>
            <Tab active={activeTab === 'timeoff'} onClick={() => onSetActiveTab('timeoff')}>
              Time Off {pendingTimeOff.length > 0 && <DraftBadge>{pendingTimeOff.length}</DraftBadge>}
            </Tab>
            <Tab active={activeTab === 'trades'} onClick={() => onSetActiveTab('trades')}>
              Trades {pendingTrades.length > 0 && <DraftBadge>{pendingTrades.length}</DraftBadge>}
            </Tab>
          </TabBar>
        </TopBarLeft>

        {activeTab === 'schedule' && (
          <TopBarRight>
            <WeekNav>
              <NavBtn onClick={onPrevWeek} title="Previous week">‹</NavBtn>
              <WeekLabel>{weekLabel}</WeekLabel>
              <NavBtn onClick={onNextWeek} title="Next week">›</NavBtn>
            </WeekNav>
            <TodayBtn onClick={onGoToToday}>Today</TodayBtn>
            <TodayBtn onClick={() => onOpenEmployeeModal()}>+ Employee</TodayBtn>
            <TodayBtn onClick={() => onOpenRoleModal()}>+ Role</TodayBtn>
            <AddBtn onClick={() => onOpenCreateShift()}>+ Add Shift</AddBtn>
            <PublishBtn
              disabled={draftCount === 0 || publishing}
              onClick={onOpenPublishConfirm}
            >
              {publishing ? 'Publishing…' : '▶ Publish'}
              {draftCount > 0 && <DraftBadge>{draftCount}</DraftBadge>}
            </PublishBtn>
          </TopBarRight>
        )}
      </TopBar>

      {/* ── Content ── */}
      <Content>
        {/* ── Schedule Grid ── */}
        {activeTab === 'schedule' && (
          <GridWrapper>
            <Grid>
              {/* Header */}
              <GridCorner>
                <CornerLabel>Staff</CornerLabel>
              </GridCorner>
              {weekDays.map(day => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const suggestion = getStaffingSuggestion(dateStr);
                const today = isToday(day);
                return (
                  <DayHeader
                    key={dateStr}
                    isToday={today}
                    onClick={() => onOpenCreateShift(dateStr)}
                    title={`Add shift on ${format(day, 'EEE MMM d')}`}
                  >
                    <DayName isToday={today}>{format(day, 'EEE')}</DayName>
                    <DayNum isToday={today}>{format(day, 'd')}</DayNum>
                    {suggestion.covers > 0 && (
                      <CoverPill warn={suggestion.understaffed}>
                        {suggestion.understaffed ? '⚠' : '👥'} {suggestion.covers} covers
                        {suggestion.understaffed && ` · need ${suggestion.suggested}`}
                      </CoverPill>
                    )}
                  </DayHeader>
                );
              })}

              {/* Employee rows */}
              {employees.map(emp => {
                const primaryRole = emp.employee_roles?.find(er => er.is_primary)?.role
                  || emp.employee_roles?.[0]?.role;
                return (
                  <EmployeeRow key={emp.id}>
                    <EmployeeCell>
                      <EmployeeName
                        style={{ cursor: 'pointer' }}
                        onClick={() => onOpenEmployeeModal(emp)}
                        title="Edit employee"
                      >
                        {emp.first_name} {emp.last_name}
                      </EmployeeName>
                      {primaryRole && (
                        <EmployeeRole style={{ color: primaryRole.color }}>
                          {primaryRole.name}
                        </EmployeeRole>
                      )}
                    </EmployeeCell>
                    {weekDays.map(day => {
                      const dateStr = format(day, 'yyyy-MM-dd');
                      const cellShifts = shiftIndex[`${emp.id}|${dateStr}`] || [];
                      return (
                        <ShiftCell
                          key={dateStr}
                          onClick={() => onOpenCreateShift(dateStr, emp.id)}
                          title={`Add shift for ${emp.first_name}`}
                        >
                          {cellShifts.map(shift => {
                            const role = roles.find(r => r.id === shift.role_id);
                            return (
                              <ShiftCard
                                key={shift.id}
                                roleColor={role?.color}
                                draft={shift.status === 'draft'}
                                onClick={e => {
                                  e.stopPropagation();
                                  onOpenEditShift(shift);
                                }}
                                title={`${role?.name || ''} · ${shift.start_time?.slice(0,5)}–${shift.end_time?.slice(0,5)}`}
                              >
                                <div>
                                  <div>{role?.name || 'Shift'}</div>
                                  <ShiftTime>
                                    {shift.start_time?.slice(0,5)}–{shift.end_time?.slice(0,5)}
                                  </ShiftTime>
                                </div>
                                {shift.status === 'draft' && <DraftDot title="Draft" />}
                              </ShiftCard>
                            );
                          })}
                          {cellShifts.length === 0 && (
                            <AddShiftBtn
                              onClick={e => { e.stopPropagation(); onOpenCreateShift(dateStr, emp.id); }}
                              title="Add shift"
                            >
                              +
                            </AddShiftBtn>
                          )}
                        </ShiftCell>
                      );
                    })}
                  </EmployeeRow>
                );
              })}

              {employees.length === 0 && (
                <div style={{ gridColumn: '1 / -1', padding: '40px', textAlign: 'center', color: COLOR.textMuted }}>
                  No employees yet.{' '}
                  <span
                    style={{ color: COLOR.strawberry, cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => onOpenEmployeeModal()}
                  >
                    Add your first employee
                  </span>{' '}
                  to get started.
                </div>
              )}
            </Grid>
          </GridWrapper>
        )}

        {/* ── Time-Off Panel ── */}
        {activeTab === 'timeoff' && (
          <PanelWrapper>
            <PanelTitle>Time-Off Requests</PanelTitle>
            <RequestList>
              {timeOffRequests.length === 0 && (
                <EmptyState>No time-off requests.</EmptyState>
              )}
              {timeOffRequests.map(req => (
                <RequestCard key={req.id}>
                  <RequestInfo>
                    <RequestName>
                      {req.employee?.first_name} {req.employee?.last_name}
                    </RequestName>
                    <RequestMeta>
                      {req.start_date} → {req.end_date}
                      {req.reason && ` · ${req.reason}`}
                    </RequestMeta>
                  </RequestInfo>
                  <ActionBtns>
                    <StatusBadge status={req.status}>{req.status}</StatusBadge>
                    {req.status === 'pending' && (
                      <>
                        <ApproveBtn onClick={() => onReviewTimeOff(req.id, 'approved', 1)}>
                          Approve
                        </ApproveBtn>
                        <DenyBtn onClick={() => onReviewTimeOff(req.id, 'denied', 1)}>
                          Deny
                        </DenyBtn>
                      </>
                    )}
                  </ActionBtns>
                </RequestCard>
              ))}
            </RequestList>
          </PanelWrapper>
        )}

        {/* ── Shift Trades Panel ── */}
        {activeTab === 'trades' && (
          <PanelWrapper>
            <PanelTitle>Shift Trades</PanelTitle>
            <RequestList>
              {shiftTrades.length === 0 && (
                <EmptyState>No shift trade requests.</EmptyState>
              )}
              {shiftTrades.map(trade => (
                <RequestCard key={trade.id}>
                  <RequestInfo>
                    <RequestName>
                      {trade.from_employee?.first_name} → {trade.to_employee?.first_name || 'Open'}
                    </RequestName>
                    <RequestMeta>
                      {trade.shift?.shift_date} · {trade.shift?.start_time?.slice(0,5)}–{trade.shift?.end_time?.slice(0,5)}
                      {trade.shift?.role && ` · ${trade.shift.role.name}`}
                      {trade.reason && ` · "${trade.reason}"`}
                    </RequestMeta>
                  </RequestInfo>
                  <ActionBtns>
                    <StatusBadge status={trade.status === 'manager_review' ? 'pending' : trade.status}>
                      {trade.status}
                    </StatusBadge>
                    {trade.status === 'manager_review' && (
                      <>
                        <ApproveBtn onClick={() => onReviewTrade(trade.id, 'approved', 1)}>
                          Approve
                        </ApproveBtn>
                        <DenyBtn onClick={() => onReviewTrade(trade.id, 'rejected', 1)}>
                          Reject
                        </DenyBtn>
                      </>
                    )}
                  </ActionBtns>
                </RequestCard>
              ))}
            </RequestList>
          </PanelWrapper>
        )}
      </Content>

      {/* ── Shift Create/Edit Modal ── */}
      <Modal isOpen={shiftModalOpen} onRequestClose={onCloseShiftModal} style={modalStyle}>
        <ModalHeader>
          <ModalTitle>{editingShift ? 'Edit Shift' : 'Add Shift'}</ModalTitle>
          <CloseBtn onClick={onCloseShiftModal}>×</CloseBtn>
        </ModalHeader>
        <ModalBody>
          <FieldGroup>
            <Label>Employee</Label>
            <Select
              value={shiftFormData.employee_id}
              onChange={e => onShiftFormChange('employee_id', Number(e.target.value))}
            >
              <option value="">Select employee</option>
              {employees.map(e => (
                <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>
              ))}
            </Select>
          </FieldGroup>
          <FieldGroup>
            <Label>Role</Label>
            <Select
              value={shiftFormData.role_id}
              onChange={e => onShiftFormChange('role_id', Number(e.target.value))}
            >
              <option value="">Select role</option>
              {roles.map(r => (
                <option key={r.id} value={r.id}>{r.name} ({r.department})</option>
              ))}
            </Select>
          </FieldGroup>
          <FieldGroup>
            <Label>Date</Label>
            <Input
              type="date"
              value={shiftFormData.shift_date}
              onChange={e => onShiftFormChange('shift_date', e.target.value)}
            />
          </FieldGroup>
          <FieldRow>
            <FieldGroup>
              <Label>Start Time</Label>
              <Input
                type="time"
                value={shiftFormData.start_time}
                onChange={e => onShiftFormChange('start_time', e.target.value)}
              />
            </FieldGroup>
            <FieldGroup>
              <Label>End Time</Label>
              <Input
                type="time"
                value={shiftFormData.end_time}
                onChange={e => onShiftFormChange('end_time', e.target.value)}
              />
            </FieldGroup>
          </FieldRow>
          <FieldGroup>
            <Label>Break (mins)</Label>
            <Input
              type="number"
              value={shiftFormData.break_duration_mins}
              min={0}
              max={120}
              step={15}
              onChange={e => onShiftFormChange('break_duration_mins', Number(e.target.value))}
            />
          </FieldGroup>
          <FieldGroup>
            <Label>Notes</Label>
            <TextArea
              value={shiftFormData.notes}
              placeholder="Optional notes for this shift…"
              onChange={e => onShiftFormChange('notes', e.target.value)}
            />
          </FieldGroup>
        </ModalBody>
        <ModalFooter>
          {editingShift ? (
            <DeleteShiftBtn onClick={() => onDeleteShift(editingShift.id)}>Delete</DeleteShiftBtn>
          ) : (
            <CancelBtn onClick={onCloseShiftModal}>Cancel</CancelBtn>
          )}
          <SaveBtn disabled={saving} onClick={onSaveShift}>
            {saving ? 'Saving…' : editingShift ? 'Update Shift' : 'Add Shift'}
          </SaveBtn>
        </ModalFooter>
      </Modal>

      {/* ── Publish Confirm Modal ── */}
      <Modal isOpen={publishConfirmOpen} onRequestClose={onClosePublishConfirm} style={modalStyle}>
        <ConfirmModal>
          <ConfirmTitle>Publish Schedule?</ConfirmTitle>
          <ConfirmText>
            This will publish <strong>{draftCount} draft shift{draftCount !== 1 ? 's' : ''}</strong> for
            the week and send Telegram notifications to all scheduled staff.
          </ConfirmText>
          <ConfirmActions>
            <CancelBtn onClick={onClosePublishConfirm}>Cancel</CancelBtn>
            <SaveBtn disabled={publishing} onClick={onPublishWeek}>
              {publishing ? 'Publishing…' : '▶ Publish & Notify'}
            </SaveBtn>
          </ConfirmActions>
        </ConfirmModal>
      </Modal>

      {/* ── Employee Modal ── */}
      <Modal isOpen={employeeModalOpen} onRequestClose={onCloseEmployeeModal} style={modalStyle}>
        <ModalHeader>
          <ModalTitle>{editingEmployee ? 'Edit Employee' : 'Add Employee'}</ModalTitle>
          <CloseBtn onClick={onCloseEmployeeModal}>×</CloseBtn>
        </ModalHeader>
        <ModalBody>
          <FieldRow>
            <FieldGroup>
              <Label>First Name</Label>
              <Input value={employeeForm.first_name} onChange={e => onEmployeeFormChange('first_name', e.target.value)} placeholder="Jane" />
            </FieldGroup>
            <FieldGroup>
              <Label>Last Name</Label>
              <Input value={employeeForm.last_name} onChange={e => onEmployeeFormChange('last_name', e.target.value)} placeholder="Smith" />
            </FieldGroup>
          </FieldRow>
          <FieldGroup>
            <Label>Phone Number</Label>
            <Input type="tel" value={employeeForm.phone} onChange={e => onEmployeeFormChange('phone', e.target.value)} placeholder="+1 (555) 000-0000" />
          </FieldGroup>
          <FieldGroup>
            <Label>Email (optional)</Label>
            <Input type="email" value={employeeForm.email} onChange={e => onEmployeeFormChange('email', e.target.value)} placeholder="jane@example.com" />
          </FieldGroup>
          <FieldRow>
            <FieldGroup>
              <Label>Wage</Label>
              <Input type="number" step="0.01" min="0" value={employeeForm.wage} onChange={e => onEmployeeFormChange('wage', e.target.value)} placeholder="18.00" />
            </FieldGroup>
            <FieldGroup>
              <Label>Wage Type</Label>
              <Select value={employeeForm.wage_type} onChange={e => onEmployeeFormChange('wage_type', e.target.value)}>
                <option value="hourly">Hourly</option>
                <option value="salary">Salary</option>
              </Select>
            </FieldGroup>
          </FieldRow>
          <FieldGroup>
            <Label>Roles</Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
              {roles.map(role => {
                const selected = employeeForm.role_ids.includes(role.id);
                return (
                  <button
                    key={role.id}
                    type="button"
                    onClick={() => {
                      const ids = selected
                        ? employeeForm.role_ids.filter(id => id !== role.id)
                        : [...employeeForm.role_ids, role.id];
                      onEmployeeFormChange('role_ids', ids);
                      if (!ids.includes(employeeForm.primary_role_id)) onEmployeeFormChange('primary_role_id', ids[0] || '');
                    }}
                    style={{
                      padding: '4px 10px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer', border: `2px solid ${role.color || '#ccc'}`,
                      background: selected ? role.color || '#ccc' : 'transparent',
                      color: selected ? '#fff' : role.color || '#555',
                    }}
                  >
                    {role.name}
                  </button>
                );
              })}
              {roles.length === 0 && <span style={{ fontSize: '12px', color: '#999' }}>No roles yet — add one first</span>}
            </div>
          </FieldGroup>
          {employeeForm.role_ids.length > 1 && (
            <FieldGroup>
              <Label>Primary Role</Label>
              <Select value={employeeForm.primary_role_id} onChange={e => onEmployeeFormChange('primary_role_id', Number(e.target.value))}>
                {employeeForm.role_ids.map(id => {
                  const r = roles.find(r => r.id === id);
                  return r ? <option key={id} value={id}>{r.name}</option> : null;
                })}
              </Select>
            </FieldGroup>
          )}
        </ModalBody>
        <ModalFooter>
          {editingEmployee ? (
            <DeleteShiftBtn onClick={() => { onDeleteEmployee(editingEmployee.id); onCloseEmployeeModal(); }}>Remove</DeleteShiftBtn>
          ) : (
            <CancelBtn onClick={onCloseEmployeeModal}>Cancel</CancelBtn>
          )}
          <SaveBtn onClick={onSaveEmployee}>
            {editingEmployee ? 'Save Changes' : 'Add Employee'}
          </SaveBtn>
        </ModalFooter>
      </Modal>

      {/* ── Role Modal ── */}
      <Modal isOpen={roleModalOpen} onRequestClose={onCloseRoleModal} style={modalStyle}>
        <ModalHeader>
          <ModalTitle>{editingRole ? 'Edit Role' : 'Add Role'}</ModalTitle>
          <CloseBtn onClick={onCloseRoleModal}>×</CloseBtn>
        </ModalHeader>
        <ModalBody>
          <FieldGroup>
            <Label>Role Name</Label>
            <Input value={roleForm.name} onChange={e => onRoleFormChange('name', e.target.value)} placeholder="e.g. Bartender" />
          </FieldGroup>
          <FieldGroup>
            <Label>Department (optional)</Label>
            <Input value={roleForm.department} onChange={e => onRoleFormChange('department', e.target.value)} placeholder="e.g. Front of House" />
          </FieldGroup>
          <FieldRow>
            <FieldGroup>
              <Label>Colour</Label>
              <Input type="color" value={roleForm.color} onChange={e => onRoleFormChange('color', e.target.value)} style={{ height: '38px', padding: '2px 4px', cursor: 'pointer' }} />
            </FieldGroup>
            <FieldGroup>
              <Label>Default Hourly Rate</Label>
              <Input type="number" step="0.01" min="0" value={roleForm.hourly_rate} onChange={e => onRoleFormChange('hourly_rate', e.target.value)} placeholder="18.00" />
            </FieldGroup>
          </FieldRow>
        </ModalBody>
        <ModalFooter>
          {editingRole ? (
            <DeleteShiftBtn onClick={() => { onDeleteRole(editingRole.id); onCloseRoleModal(); }}>Delete</DeleteShiftBtn>
          ) : (
            <CancelBtn onClick={onCloseRoleModal}>Cancel</CancelBtn>
          )}
          <SaveBtn onClick={onSaveRole}>
            {editingRole ? 'Save Changes' : 'Add Role'}
          </SaveBtn>
        </ModalFooter>
      </Modal>
    </Page>
  );
};

export default SchedulingView;
